<template>
  <div class="ImportForm">
    <Steps :hasListedSteps="true">
      <template #0>
        <section :title="$t('orderDetails')">
          <Row>
            <Column :xs="8">
              <Field
                :label="$t('projectId')"
                required
                :value.sync="projectId" />
            </Column>
            <Column :xs="4">
              <Btn
                class="fetchDirecto"
                :disabled="!projectId"
                :isLoading="isFetchingDirecto"
                @click="getOrderFromDirecto">
                {{ $t('fetchDirecto') }}
              </Btn>
            </Column>
          </Row>
          <Row>
            <Column :lg="6">
              <Field
                disabled
                :label="$t('date')"
                :value.sync="orderDetails.date" />
            </Column>
            <Column :lg="6">
              <Field
                disabled
                :label="$t('orderStatus')"
                :value.sync="orderStatusText" />
            </Column>
          </Row>
          <Row>
            <Column :lg="6">
              <Field
                disabled
                :label="$t('clientName')"
                :value.sync="orderDetails.customerName" />
            </Column>
            <Column :lg="6">
              <Field
                disabled
                :label="$t('clientId')"
                :value.sync="orderDetails.customerId" />
            </Column>
          </Row>
        </section>
      </template>
      <template #1>
        <section :title="$t('itemsList')">
          <Field
            class="fullHeight"
            :label="$t('inputExcel')"
            required
            type="textarea"
            :value.sync="inputExcel" />
        </section>
      </template>
      <template #1-next>
        <Btn type="submit" @click.native="parse($event)">
          {{ $t('continue') }}
        </Btn>
      </template>
      <template #2>
        <section class="filledHeight" :title="$t('sendToDirecto')">
          <h4 id="preview" class="label">
            {{ $t('preview') }}
          </h4>
          <Tabs>
            <Sect slot="JSON">
              <Field
                class="fullHeight"
                disabled
                type="textarea"
                :value.sync="preview" />
            </Sect>
            <Sect slot="XML">
              <Field
                class="fullHeight"
                disabled
                type="textarea"
                :value.sync="jsonToXML" />
            </Sect>
          </Tabs>
          <Field
            :label="$t('checkToVerify')"
            type="checkbox"
            :value.sync="isVerified" />
        </section>
      </template>
      <template #2-next>
        <Btn
          class="sendToDirecto"
          :disabled="!isVerified"
          :isLoading="isSendingToDirecto"
          type="submit"
          @click="sendToDirecto">
          {{ $t('sendToDirecto') }}
        </Btn>
      </template>
    </Steps>
  </div>
</template>

<script>
import { actions, getters } from '../../utils'
import Steps from 'components/Steps'
import Tabs from 'components/Tabs'
import builder from 'xmlbuilder'

export default {
  components: {
    Tabs,
    Steps,
  },
  data() {
    return {
      projectId: '',
      rowsExisting: [],
      orderDetails: {
        orderNo: null,
        customerId: null,
        customerName: null,
        date: null,
        status: null,
      },
      inputExcel: null,
      preview: null,
      //
      isVerified: false,
      isFetchingDirecto: false,
      isSendingToDirecto: false,
      productsScoro: [],
    }
  },
  computed: {
    orderStatusText() {
      if (this.orderDetails.status === null) return ''
      return this.orderDetails.status ? 'NEPRADĖTAS' : 'PRADĖTAS'
    },
    jsonToXML() {
      const xml = builder.create('orders')
        .ele('order', {
          cust_order_no: this.orderDetails.orderNo,
          param: 1, // UPDATE, NOT OVERWRITE
        })

      if (!this.preview) return

      JSON.parse(this.preview).forEach((row, index) => {
        const { rn, rv, code, quantity } = row
        xml.ele('line', {
          rn,
          rv,
          pricewovat: 0,
          quantity,
          code,
        })
      })

      return xml.end({ pretty: true })
    },
    ...getters(),
  },
  methods: {
    parse(event) {
      try {
        if (!this.inputExcel) throw new Error(this.$t('inputCannotBeEmpty'))

        const result = this.inputExcel
          .trim('\n')
          .split('\n')
          .map(row => {
            const columns = row
              .replace(/(\t)\1+/g, '\t')
              .split('\t')

            const [
              rn,
              rv,
              code,
              label,
              quantity,
            ] = columns

            if (!code || label === 'Produkto pavadinimas, kodas' || columns.length < 5) return

            return {
              rn,
              rv,
              code,
              label,
              quantity: parseFloat(quantity),
            }
          })
          .filter(Boolean)
          .filter(row => row.code)

        if (result.length === 0) throw new Error(this.$t('badInputFormat'))

        const lastExistingRn = this.rowsExisting[this.rowsExisting.length - 1].rn
        const productsWithLineNumbersAdjusted = this.adjustLineNumbers(result, lastExistingRn)

        this.preview = JSON.stringify(productsWithLineNumbersAdjusted, null, '  ')
      } catch (error) {
        event.preventDefault()
        this.ERROR(error)
      }
    },
    adjustLineNumbers(products, numberLast) {
      return products.map(product => ({
        ...product,
        rn: Number(product.rn) + Number(numberLast),
        rv: Number(product.rv) + Number(numberLast),
      }))
    },
    clearClientData() {
      this.rowsExisting = []
      this.orderDetails.orderNo = null
      this.orderDetails.customerId = null
      this.orderDetails.customerName = null
      this.orderDetails.date = null
      this.orderDetails.status = null
      this.inputExcel = null
      this.preview = null
    },
    async getOrderFromDirecto() {
      this.clearClientData()

      try {
        this.isFetchingDirecto = true
        const order = await this.GET_DIRECTO(this.projectId)

        if (!order.transport.sales_orders) {
          this.ERROR('Užsakymo duomenys negauti')
          return
        }
        const orderDetails = order.transport.sales_orders[0].$

        const rows = order.transport.sales_orders[0].rows[0].row.map(row => row.$)
        this.rowsExisting = rows.sort((a, b) => {
          return a.rn - b.rn
        }) // SORTED BY RN

        this.$set(this.orderDetails, 'orderNo', orderDetails.cust_order_no)

        this.$set(this.orderDetails, 'customerId', orderDetails.customer)

        this.$set(this.orderDetails, 'customerName', orderDetails.customer_name)

        this.$set(this.orderDetails, 'date', orderDetails.date)

        this.$set(this.orderDetails, 'status', orderDetails.order_status)
      } catch (error) {
        if (!error.ignore) {
          this.ERROR(error)
        }
      } finally {
        this.isFetchingDirecto = false
      }
    },
    async sendToDirecto() {
      this.isSendingToDirecto = true

      try {
        await this.CREATE_DIRECTO({ action: 'update', xmlData: this.jsonToXML })
        this.SUCCESS('Sėkmingas papildymas')
      } catch (error) {
        if (!error.ignore) {
          this.ERROR(error)
        }
      } finally {
        this.isSendingToDirecto = false
      }
    },
    ...actions(
      'CREATE_DIRECTO',
      'GET_DIRECTO',
    ),
  },
}
</script>

<style lang="scss">
.ImportForm {
  height: 100%;

  .sendToDirecto {
    text-transform: uppercase;
  }

  .Btn.fetchDirecto {
    margin-top: 2.5rem;
  }

  .Steps {
    display: flex;
    flex-direction: column;
    height: 100%;

    > form {
      height: 100%;

      > span {
        display: flex;
        flex-direction: column;
        height: 100%;

        > .current {
          height: 100%;
        }

        section {
          height: 100%;

          .addressContainer {
            height: 10rem;

            .Field {
              height: 100%;

              textarea {
                max-height: 100%;
                min-height: unset;
              }
            }
          }

          .fullHeight {
            display: flex;
            flex-direction: column;
            height: 100%;

            > .Field {
              height: 100%;

              textarea {
                height: 100%;
              }
            }
          }
        }
      }
    }
  }

  h4.label {
    line-height: 2.5rem;
  }

  .filledHeight {
    display: flex;
    flex-direction: column;

    .Tabs {
      display: flex;
      flex-direction: column;
      height: 100%;

      .body {
        height: 100%;

        .Sect {
          height: 100%;
        }
      }
    }

    > .fullHeight {
      display: flex;
      flex-direction: column;
      height: 100%;

      .Field {
        height: 100%;

        textarea {
          height: 100%;
          white-space: pre;
        }
      }
    }
  }
}
</style>

<i18n>
{
  "en": {
    "orderStatus": "Užsakymo vykdymas",
    "inputExcel": "Lentelė iš Excel",
    "scoroProducts": "Prekės iš Scoro",
    "itemsList": "Kitos prekės",
    "orderDetails": "Užsakymas",
    "sendToDirecto": "Siųsti į Directo",
    "preview": "Peržiūra",
    "date": "Data",
    "projectId": "Projekto ID (Scoro Order ID)",
    "managerId": "Vadybininkas",
    "address": "Adresas",
    "clientId": "Kliento ID",
    "clientName": "Klientas",
    "fetchDirecto": "Gauti iš Directo",
    "checkToVerify": "Patvirtinti",
    "parse": "Parse",
    "badInputFormat": "Netinkamas formatas",
    "continue": "Tęsti",
    "inputCannotBeEmpty": "Laukas negali būti tuščias"
  }
}
</i18n>
